import axios, {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  type CreateAxiosDefaults,
  Method,
  type RawAxiosRequestHeaders,
} from 'axios';
import { toApiError } from '@frodo/lib/services/http/error/helpers';

abstract class ApiHttpBaseService {
  protected readonly apiClient: AxiosInstance;

  protected constructor(
    baseUrl: string | undefined,
    headers: RawAxiosRequestHeaders,
    axiosConfig: CreateAxiosDefaults,
  ) {
    this.apiClient = axios.create({
      adapter: 'fetch',
      ...axiosConfig,
      baseURL: baseUrl,
      headers,
    });

    this.apiClient.interceptors.response.use(
      (response: AxiosResponse) => response,
      (error: unknown): Promise<never> => Promise.reject(toApiError(error)),
    );
  }

  protected async request<T, U>(
    method: Method,
    url: string,
    data?: T,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<U>> {
    return this.apiClient.request<U>({
      method,
      url,
      data,
      ...config,
    });
  }

  protected async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response: AxiosResponse<T> = await this.apiClient.get<T>(url, config);

    return response.data;
  }

  protected async post<T, U>(
    url: string,
    data?: T,
    config?: AxiosRequestConfig,
  ): Promise<U> {
    const response: AxiosResponse<U> = await this.apiClient.post<U>(url, data, {
      ...config,
      headers: { 'Content-Type': 'application/json', ...config?.headers },
    });

    return response.data;
  }

  protected async put<T, U>(
    url: string,
    data: T,
    config?: AxiosRequestConfig,
  ): Promise<U> {
    const response: AxiosResponse<U> = await this.apiClient.put<U>(url, data, {
      ...config,
      headers: { 'Content-Type': 'application/json', ...config?.headers },
    });

    return response.data;
  }

  protected async delete<T>(
    url: string,
    config?: AxiosRequestConfig,
  ): Promise<T> {
    const response: AxiosResponse<T> = await this.apiClient.delete<T>(
      url,
      config,
    );

    return response.data;
  }
}

export default ApiHttpBaseService;
